import { useCallback, useEffect, useState } from "react";
import { messageService } from "@/services/messageService";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Tracks the signed-in user's unread message count for header badges.
 * Uses realtime updates when available and a lightweight poll as a fallback.
 */
export function useUnreadMessages() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) {
      setCount(0);
      return;
    }
    try {
      const c = await messageService.unreadCount(user.id);
      setCount(c);
    } catch {
      /* keep last known value */
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return undefined;
    }
    refresh();
    const unsub = messageService.subscribe(user.id, () => refresh());
    const poll = setInterval(refresh, 25000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      unsub?.();
      clearInterval(poll);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, refresh]);

  return { count, refresh };
}

export default useUnreadMessages;
